import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import StickyBooking from '../../components/feature/StickyBooking';
import { pathFor, type Lang } from '../../router/paths';

const ambienteImages = [
  '/images/ambiente/ambiente-01.jpg',
  '/images/ambiente/ambiente-02.jpg',
  '/images/ambiente/ambiente-03.jpg',
  '/images/ambiente/ambiente-04.jpg',
  '/images/ambiente/ambiente-05.jpg',
  '/images/ambiente/ambiente-06.jpg',
];

export default function AufenthaltPage() {
  const [activeImg, setActiveImg] = useState<string | null>(null);
  const [kiniHovered, setKiniHovered] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';

  const handleMouseEnter = () => {
    setKiniHovered(true);
    if (!audioRef.current) {
      audioRef.current = new Audio('https://upload.wikimedia.org/wikipedia/commons/7/7e/Bayerischer_Defiliermarsch.ogg');
      audioRef.current.volume = 0.4;
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const handleMouseLeave = () => {
    setKiniHovered(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <StickyBooking />

      {/* Hero */}
      {/* MOBILE: h-[75vh] statt 110vh */}
      <section className="relative h-[75vh] sm:h-[80vh] sm:min-h-[640px] flex items-end sm:items-center justify-center overflow-hidden pb-12 sm:pb-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Traditional%20Bavarian%20breakfast%20spread&width=1920&height=1080&seq=aufenthalt-hero-breakfast&orientation=landscape"
            alt="Bayerisches Frühstück im Hotel Alpenhof Gauting nahe München"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 text-left sm:pt-44">
          <div className="max-w-5xl mx-auto text-white">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 sm:mb-4 text-amber-300">{t('aufHeroLabel')}</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif leading-snug sm:leading-tight mb-3 sm:mb-6">
              {t('aufHeroTitle').split('\\n').map((line, i, arr) => (
                <span key={i} className={i === arr.length - 1 ? 'text-amber-200' : ''}>{line}<br /></span>
              ))}
            </h1>
            <p className="text-sm sm:text-lg text-white/80 leading-relaxed max-w-xl sm:mx-0 mx-auto">{t('aufHeroText')}</p>
          </div>
        </div>
      </section>

      {/* Frühstück */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6">{t('aufBreakfastTitle')}</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">{t('aufBreakfastText')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-8 mb-16">
            {[
              { titleKey: 'aufCard1Title', textKey: 'aufCard1Text', icon: 'ri-plant-line' },
              { titleKey: 'aufCard2Title', textKey: 'aufCard2Text', icon: 'ri-cup-line' },
              { titleKey: 'aufCard3Title', textKey: 'aufCard3Text', icon: 'ri-heart-line' },
            ].map(({ titleKey, textKey, icon }) => (
              <div key={titleKey} className="text-center p-4 sm:p-8 bg-white rounded-xl sm:rounded-2xl shadow-sm hover:shadow-xl transition-shadow">
                <div className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-3 sm:mb-5 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full">
                  <i className={`${icon} text-xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="text-sm sm:text-xl font-semibold text-gray-800 mb-1.5 sm:mb-3">{t(titleKey)}</h3>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{t(textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aufenthalts-Highlights */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6">{t('aufStayTitle')}</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">{t('aufStayText')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              { titleKey: 'aufHighlight1Title', textKey: 'aufHighlight1Text', icon: 'ri-leaf-line', color: 'from-emerald-500 to-emerald-700' },
              { titleKey: 'aufHighlight2Title', textKey: 'aufHighlight2Text', icon: 'ri-home-heart-line', color: 'from-amber-500 to-amber-700' },
              { titleKey: 'aufHighlight3Title', textKey: 'aufHighlight3Text', icon: 'ri-user-smile-line', color: 'from-blue-500 to-blue-700' },
              { titleKey: 'aufHighlight4Title', textKey: 'aufHighlight4Text', icon: 'ri-map-pin-line', color: 'from-red-500 to-red-700' },
            ].map(({ titleKey, textKey, icon, color }) => (
              <div key={titleKey} className="bg-stone-50 p-4 sm:p-8 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-xl transition-shadow">
                <div className={`w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center mb-3 sm:mb-5 bg-gradient-to-br ${color} rounded-full`}>
                  <i className={`${icon} text-lg sm:text-2xl text-white`}></i>
                </div>
                <h3 className="text-sm sm:text-xl font-semibold text-gray-800 mb-1.5 sm:mb-3">{t(titleKey)}</h3>
                <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{t(textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Praktische Informationen */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-4">{t('aufInfoTitle')}</h2>
            <p className="text-gray-600">{t('aufInfoSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
            {[
              { titleKey: 'aufInfo1Title', icon: 'ri-time-line', content: <><p className="text-gray-600 text-xs sm:text-sm mb-1"><strong>Check-in:</strong> {t('aufInfo1In')}</p><p className="text-gray-600 text-xs sm:text-sm"><strong>Check-out:</strong> {t('aufInfo1Out')}</p><p className="text-gray-500 text-xs mt-1.5 sm:mt-2">{t('aufInfo1Note')}</p></> },
              { titleKey: 'aufInfo2Title', icon: 'ri-restaurant-line', content: <><p className="text-gray-600 text-xs sm:text-sm mb-1"><strong>Mo – Fr:</strong> {t('aufInfo2Weekday')}</p><p className="text-gray-600 text-xs sm:text-sm"><strong>Sa &amp; So:</strong> {t('aufInfo2Weekend')}</p><p className="text-gray-500 text-xs mt-1.5 sm:mt-2">{t('aufInfo2Note')}</p></> },
              { titleKey: 'aufInfo3Title', icon: 'ri-parking-box-line', content: <><p className="text-gray-600 text-xs sm:text-sm mb-1">{t('aufInfo3Text')}</p><p className="text-gray-500 text-xs mt-1.5 sm:mt-2">{t('aufInfo3Note')}</p></> },
              { titleKey: 'aufInfo4Title', icon: 'ri-wifi-line', content: <><p className="text-gray-600 text-xs sm:text-sm mb-1">{t('aufInfo4Text')}</p><p className="text-gray-500 text-xs mt-1.5 sm:mt-2">{t('aufInfo4Note')}</p></> },
            ].map(({ titleKey, icon, content }) => (
              <div key={titleKey} className="border-2 border-stone-200 rounded-xl sm:rounded-2xl p-4 sm:p-8 bg-white hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex-shrink-0">
                    <i className={`${icon} text-base sm:text-xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2">{t(titleKey)}</h3>
                    {content}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bildergalerie */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-4">{t('aufGalleryTitle')}</h2>
            <p className="text-gray-600">{t('aufGalleryText')}</p>
          </div>
          <div className="columns-2 md:columns-3 gap-4 space-y-4">
            {ambienteImages.map((src, i) => (
              <div key={i} className="break-inside-avoid overflow-hidden rounded-2xl cursor-pointer group shadow-sm hover:shadow-xl transition-shadow" onClick={() => setActiveImg(src)}>
                <img src={src} alt={`Ambiente Hotel Alpenhof Gauting – Eindruck ${i + 1}`} loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-amber-700 relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-amber-200 mb-5">{t('aufCtaLabel')}</p>
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight">{t('aufCtaTitle')}</h2>
          <p className="text-lg text-amber-100 leading-relaxed mb-10 max-w-2xl mx-auto">{t('aufCtaText')}</p>
          <button
            onClick={() => navigate(pathFor('buchen', lang))}
            className="inline-block bg-white text-amber-700 px-10 py-4 rounded-md text-sm font-semibold hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            {t('aufCtaButton')}
          </button>
        </div>
      </section>

      {activeImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setActiveImg(null)}>
          <button className="absolute top-5 right-6 text-white text-4xl cursor-pointer hover:text-amber-300 transition-colors" onClick={() => setActiveImg(null)}>
            <i className="ri-close-line"></i>
          </button>
          <img src={activeImg} alt="Hotel Alpenhof Gauting – Detailansicht" className="max-w-4xl max-h-[90vh] w-full object-contain rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <Footer />
    </div>
  );
}
